/**
 * Transforms frontend EvalFormData into the DTO shape expected by the backend.
 * LLM scorers use a nested `configuration` object; other scorers pass top-level fields only.
 */

import { SCORER_LLM, VARIANT_LLM_JUDGE } from '../constants/evalConstants';
import type { EvalFormData } from '../types/evalForm.types';

import type { ModelConfig } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/AgentConnectorDrawer/features/model-config/types/modelConfigForm.types';
import {
  findModelById,
  type Model
} from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/AgentConnectorDrawer/types/model.types';
import { resolveModelById } from '~/scenes/Build/scenes/FlowBuilder/components/Design/components/AgentConnectorDrawer/utils/agentConnectorModelRegistry';

const DEFAULT_LLM_MODEL_CONFIG: ModelConfig = {
  temperature: '0.7',
  maxOutputTokens: '',
  topK: '64',
  topP: '1',
  frequencyPenalty: '',
  presencePenalty: '',
  account: '',
  certificateChain: '',
  customParameters: [],
  timeout: '30000',
  modelMaxRetries: '3',
  customUrl: '',
  allowInsecureConnections: false,
  useJsonSchema: false,
  jsonSchema: '',
  useOutputFormatBody: false,
  outputFormatBody: ''
};

/** LLM eval `configuration` sent on create/update */
export interface EvalLlmConfiguration {
  llmProvider: string;
  model: string;
  systemPrompt?: string;
  accountLabel?: string;
  /** Some API responses use `account` instead of `accountLabel`. */
  account?: string;
  certificateChain?: string;
  temperature?: number;
  maxOutputTokens?: number;
  topK?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  timeout?: number;
  modelMaxRetries?: number;
  customUrl?: string;
  allowInsecureConnections?: boolean;
  customParameters?: string;
  llmTemplate?: string;
}

export interface EvalApiPayload {
  [key: string]: unknown;
  name: string;
  path: string;
  scorer: string;
  variant: string;
  configuration?: EvalLlmConfiguration;
}

function serializeCustomParameters(
  params: ModelConfig['customParameters']
): string | undefined {
  if (!params || params.length === 0) return undefined;
  const obj = params.reduce(
    (acc: Record<string, string>, { key, value }) => {
      if (key) acc[key] = value;
      return acc;
    },
    {} as Record<string, string>
  );
  const json = JSON.stringify(obj);
  return json === '{}' ? undefined : json;
}

function resolveProvider(modelId: string, catalogModels?: Model[]): string {
  const model = resolveModelById(modelId, catalogModels);
  if (model) return model.provider;
  const colonIdx = modelId.indexOf(':');
  if (colonIdx > 0) return modelId.slice(0, colonIdx);
  return '';
}

/**
 * Builds the `llmModel` value expected by the eval form / LlmModelSelect from API fields.
 * Inverse of `buildConnectorModelId`: standard providers use bare model slugs; Azure/Bedrock
 * use `provider:deployment` ids.
 */
export function composeLlmModelSelectValueFromApi(
  llmProvider: string | undefined,
  model: string | undefined
): string {
  const m = (model ?? '').trim();
  if (!m) return '';
  const p = (llmProvider ?? '').trim();
  if (!p) return m;

  if (m.startsWith(`${p}:`)) return m;

  if (findModelById(m)) return m;

  const composite = `${p}:${m}`;
  if (findModelById(composite)) return composite;

  const usesPrefixedIds = p === 'azureopenai' || p === 'amazonbedrock';
  return usesPrefixedIds ? composite : m;
}

function numToModelConfigString(
  n: number | undefined,
  fallback: string
): string {
  if (n === undefined || Number.isNaN(n)) return fallback;
  return String(n);
}

function optionalNumToString(n: number | undefined): string {
  if (n === undefined || Number.isNaN(n)) return '';
  return String(n);
}

function parseCustomParametersFromApi(
  raw: string | undefined
): ModelConfig['customParameters'] {
  if (raw == null || raw.trim() === '' || raw.trim() === '{}') return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return [];
    }
    const entries = Object.entries(parsed as Record<string, unknown>);
    return entries.map(([key, value]) => ({
      id: globalThis.crypto.randomUUID(),
      key,
      value: value == null ? '' : String(value)
    }));
  } catch {
    return [];
  }
}

/**
 * Maps API `configuration` (LLM-as-judge) back to flat eval fields used by the UI and store.
 */
export function evalLlmFlatFieldsFromConfiguration(
  configuration: EvalLlmConfiguration
): {
  llmTemplate?: string;
  llmModel: string;
  llmPrompt: string;
  llmModelConfig: ModelConfig;
} {
  const llmModel = composeLlmModelSelectValueFromApi(
    configuration.llmProvider,
    configuration.model
  );
  const llmPrompt = configuration.systemPrompt ?? '';
  const llmTemplate = configuration.llmTemplate?.trim()
    ? configuration.llmTemplate.trim()
    : undefined;

  const llmModelConfig: ModelConfig = {
    ...DEFAULT_LLM_MODEL_CONFIG,
    temperature: numToModelConfigString(
      configuration.temperature,
      DEFAULT_LLM_MODEL_CONFIG.temperature
    ),
    maxOutputTokens: numToModelConfigString(
      configuration.maxOutputTokens,
      DEFAULT_LLM_MODEL_CONFIG.maxOutputTokens
    ),
    topK: numToModelConfigString(
      configuration.topK,
      DEFAULT_LLM_MODEL_CONFIG.topK
    ),
    topP: numToModelConfigString(
      configuration.topP,
      DEFAULT_LLM_MODEL_CONFIG.topP
    ),
    frequencyPenalty: optionalNumToString(configuration.frequencyPenalty),
    presencePenalty: optionalNumToString(configuration.presencePenalty),
    account:
      configuration.accountLabel?.trim() || configuration.account?.trim() || '',
    certificateChain: configuration.certificateChain?.trim() ?? '',
    timeout: numToModelConfigString(
      configuration.timeout,
      DEFAULT_LLM_MODEL_CONFIG.timeout
    ),
    modelMaxRetries: numToModelConfigString(
      configuration.modelMaxRetries,
      DEFAULT_LLM_MODEL_CONFIG.modelMaxRetries
    ),
    customUrl: configuration.customUrl?.trim() ?? '',
    allowInsecureConnections: configuration.allowInsecureConnections === true,
    customParameters: parseCustomParametersFromApi(
      configuration.customParameters
    )
  };

  return { llmTemplate, llmModel, llmPrompt, llmModelConfig };
}

/** Backend `model` id (e.g. `gpt-4o-mini`), without `provider:` prefix when present */
function resolveModelIdForApi(
  modelId: string | undefined,
  catalogModels?: Model[]
): string {
  if (!modelId) return '';
  const found = resolveModelById(modelId, catalogModels);
  if (found) return found.id;
  const colonIdx = modelId.indexOf(':');
  if (colonIdx > 0) return modelId.slice(colonIdx + 1);
  return modelId;
}

function parseConfigNumber(value: string | undefined): number | undefined {
  if (value == null || value.trim() === '') return undefined;
  if (/\{\{[^}]*\}\}/.test(value)) return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

function buildLlmConfiguration(
  data: EvalFormData,
  catalogModels?: Model[]
): EvalLlmConfiguration {
  const config: ModelConfig | undefined = data.llmModelConfig;
  const modelId = data.llmModel || '';
  const configuration: EvalLlmConfiguration = {
    llmProvider: modelId ? resolveProvider(modelId, catalogModels) : '',
    model: resolveModelIdForApi(modelId, catalogModels),
    systemPrompt: data.llmPrompt || undefined
  };

  if (config?.account?.trim()) {
    configuration.accountLabel = config.account.trim();
  }
  if (config?.certificateChain?.trim()) {
    configuration.certificateChain = config.certificateChain.trim();
  }

  const temperature = parseConfigNumber(config?.temperature);
  if (temperature !== undefined) configuration.temperature = temperature;

  const maxOutputTokens = parseConfigNumber(config?.maxOutputTokens);
  if (maxOutputTokens !== undefined) {
    configuration.maxOutputTokens = maxOutputTokens;
  }

  const topK = parseConfigNumber(config?.topK);
  if (topK !== undefined) configuration.topK = topK;

  const topP = parseConfigNumber(config?.topP);
  if (topP !== undefined) configuration.topP = topP;

  const frequencyPenalty = parseConfigNumber(config?.frequencyPenalty);
  if (frequencyPenalty !== undefined) {
    configuration.frequencyPenalty = frequencyPenalty;
  }

  const presencePenalty = parseConfigNumber(config?.presencePenalty);
  if (presencePenalty !== undefined) {
    configuration.presencePenalty = presencePenalty;
  }

  const timeout = parseConfigNumber(config?.timeout);
  if (timeout !== undefined) configuration.timeout = timeout;

  const modelMaxRetries = parseConfigNumber(config?.modelMaxRetries);
  if (modelMaxRetries !== undefined) {
    configuration.modelMaxRetries = modelMaxRetries;
  }

  if (config?.customUrl?.trim()) {
    configuration.customUrl = config.customUrl.trim();
  }
  if (config?.allowInsecureConnections !== undefined) {
    configuration.allowInsecureConnections = config.allowInsecureConnections;
  }

  configuration.customParameters =
    serializeCustomParameters(config?.customParameters ?? []) ?? '{}';

  if (data.llmTemplate?.trim()) {
    configuration.llmTemplate = data.llmTemplate.trim();
  }

  return configuration;
}

export function transformEvalForApi(
  data: EvalFormData,
  options?: { catalogModels?: Model[] }
): EvalApiPayload {
  const base: EvalApiPayload = {
    name: data.name,
    path: data.path,
    scorer: data.scorer,
    variant: data.variant
  };

  if (data.scorer !== SCORER_LLM) return base;

  return {
    name: data.name,
    path: data.path,
    scorer: data.scorer,
    variant: VARIANT_LLM_JUDGE,
    configuration: buildLlmConfiguration(data, options?.catalogModels)
  };
}
